import { Link } from "react-router-dom";
import { Shield, FileCheck, Lock, ArrowRight, CheckCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";

const defaultServices = [
  {
    icon: Shield,
    title: "ISO/IEC 27001 Certification",
    desc: "Information security management system certification through Stage 1 and Stage 2 audits with surveillance program. Delivered in partnership with internationally accredited certification bodies.",
    features: ["Gap analysis and readiness assessment", "ISMS design and implementation support", "Stage 1 documentation review audit", "Stage 2 implementation audit", "Surveillance audit program"],
  },
  {
    icon: FileCheck,
    title: "ISO 9001 Certification",
    desc: "Quality management system certification for organizations seeking international quality standards recognition. Process-based auditing aligned with ISO 9001:2015 requirements.",
    features: ["Quality management system review", "Process approach assessment", "Performance monitoring evaluation", "Continual improvement verification", "Certification and surveillance"],
  },
  {
    icon: Shield,
    title: "ISO 22301 Certification",
    desc: "Business continuity management certification ensuring organizational resilience and recovery capability. Critical for organizations in finance, healthcare, and infrastructure.",
    features: ["Business impact analysis review", "BCM strategy assessment", "Incident response evaluation", "Recovery plan testing", "Surveillance and recertification"],
  },
  {
    icon: Lock,
    title: "ISO 27701 Certification",
    desc: "Privacy information management extending ISO 27001 to cover personal data protection requirements. Essential for GDPR and global privacy regulation compliance.",
    features: ["PII processing assessment", "Privacy controls evaluation", "Data protection impact review", "Consent management verification", "Extension to existing ISMS certification"],
  },
  {
    icon: FileCheck,
    title: "Compliance Assessments",
    desc: "Gap analysis, readiness audits, and pre-certification reviews against international standards. Identify compliance gaps and build a clear roadmap to certification.",
    features: ["Regulatory gap analysis", "Policy and procedure review", "Control effectiveness testing", "Compliance roadmap development", "Pre-audit readiness check"],
  },
  {
    icon: Lock,
    title: "Cybersecurity Assurance",
    desc: "Security operations, vulnerability assessment, penetration testing, and cloud security integrated with certification programs. End-to-end cybersecurity evaluation.",
    features: ["Penetration testing", "Vulnerability assessments", "Security architecture review", "Cloud security evaluation", "24/7 SOC monitoring services"],
  },
  {
    icon: Shield,
    title: "SOC 2 Readiness & Assessment",
    desc: "Trust Services Criteria assessment and readiness program for organizations targeting SOC 2 Type I or Type II reports. Comprehensive evaluation of security, availability, processing integrity, confidentiality, and privacy controls.",
    features: ["Trust Services Criteria gap analysis", "Control design and implementation review", "Type I / Type II readiness assessment", "Evidence collection and documentation support", "Remediation guidance and audit preparation"],
  },
];

export default function ServicesPage() {
  const { t } = useLang();
  const sp = t?.services_page || {};
  const tServices = sp.services || [];

  const services = defaultServices.map((def, i) => {
    const ts = tServices[i];
    return {
      icon: def.icon,
      title: ts?.title || def.title,
      desc: ts?.desc || def.desc,
      features: ts?.features || def.features,
    };
  });

  return (
    <div className="page-transition pt-20" data-testid="services-page">
      <section className="bg-[#0B2545] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#13A8BD] mb-4">{sp.badge || "Our Services"}</p>
          <h1 data-testid="services-page-heading" className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white leading-[1.1] mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
            {sp.title || "Certification Services"}
          </h1>
          <p className="text-base md:text-lg text-white/65 max-w-2xl leading-relaxed">
            {sp.subtitle || "As an independent certification board, GACA delivers audit and certification services through our network of accredited partner bodies across all major management system standards."}
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-0 border border-[#0B2545]/10">
            {services.map((service, i) => (
              <div key={service.title} data-testid={`service-detail-${i}`} className={`grid grid-cols-1 lg:grid-cols-2 gap-0 border-b border-[#0B2545]/10 last:border-b-0 ${i % 2 === 1 ? 'bg-[#F7F9FC]' : 'bg-white'}`}>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <service.icon size={28} className="text-[#13A8BD] mb-5" strokeWidth={1.5} />
                  <h2 className="text-xl font-bold text-[#0B2545] mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>{service.title}</h2>
                  <p className="text-sm text-slate-600 leading-relaxed">{service.desc}</p>
                </div>
                <div className="p-8 lg:p-12 border-l border-[#0B2545]/10">
                  <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-6">{sp.deliverables_label || "Key Deliverables"}</h4>
                  <ul className="space-y-3">
                    {service.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-slate-700">
                        <CheckCircle size={14} className="text-[#13A8BD] mt-0.5 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#F7F9FC] border-t border-[#0B2545]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#0B2545] mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>{sp.cta_title || "Need a Tailored Engagement?"}</h2>
          <p className="text-sm text-slate-600 max-w-xl mx-auto mb-10">{sp.cta_desc || "Every organization has unique compliance and security requirements. Contact our team to discuss a customized certification program."}</p>
          <Link to="/contact" data-testid="services-cta" className="inline-flex items-center gap-2 bg-[#13A8BD] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#0e8a9c] transition-colors">
            {sp.cta_button || "Apply for Certification"} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
